import { AppLoadingScreen } from "@/components/AppLoading"
import { BrowserRouter, Route, Routes } from "react-router-dom"
import { Suspense, lazy } from "react"
import { createPortal } from "react-dom"

import { APP_ROUTES } from "./constants/routes"
import { LayoutWrapper } from "./components/layout/LayoutWrapper"
import { MaintenanceRoute } from "./components/MaintenanceRoute"
import { ProtectedRoute } from "./components/ProtectedRoute"
import { ScrollToTop } from "@/components/ScrollToTop"
import { Toaster } from "sonner"
import { useMaintenanceMode } from "./hooks/use-maintenance-mode"

const AdCreate = lazy(() =>
  import("./pages/AdCreate").then((module) => ({ default: module.AdCreate }))
)
const AdDetails = lazy(() =>
  import("./pages/AdDetails").then((module) => ({ default: module.AdDetails }))
)
const AdEdit = lazy(() =>
  import("./pages/AdEdit").then((module) => ({ default: module.AdEdit }))
)
const Ads = lazy(() =>
  import("./pages/Ads").then((module) => ({ default: module.Ads }))
)
const Analytics = lazy(() =>
  import("./pages/Analytics").then((module) => ({ default: module.Analytics }))
)
const Auth = lazy(() =>
  import("./pages/Auth").then((module) => ({ default: module.Auth }))
)
const Dashboard = lazy(() =>
  import("./pages/Dashboard").then((module) => ({ default: module.Dashboard }))
)
const GasAccount = lazy(() =>
  import("./pages/GasAccount").then((module) => ({ default: module.GasAccount }))
)
const Maintenance = lazy(() =>
  import("./pages/Maintenance").then((module) => ({ default: module.Maintenance }))
)
const NotFound = lazy(() =>
  import("./pages/NotFound").then((module) => ({ default: module.NotFound }))
)
const Policies = lazy(() =>
  import("./pages/Policies").then((module) => ({ default: module.Policies }))
)
const PolicyCreate = lazy(() =>
  import("./pages/PolicyCreate").then((module) => ({ default: module.PolicyCreate }))
)
const PolicyDetails = lazy(() =>
  import("./pages/PolicyDetails").then((module) => ({ default: module.PolicyDetails }))
)
const Settings = lazy(() =>
  import("./pages/Settings").then((module) => ({ default: module.Settings }))
)

function RouteFallback() {
  return <AppLoadingScreen />
}

function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route
          path={APP_ROUTES.maintenance}
          element={
            <MaintenanceRoute>
              <Maintenance />
            </MaintenanceRoute>
          }
        />
        <Route
          path={APP_ROUTES.auth}
          element={
            <MaintenanceRoute>
              <Auth />
            </MaintenanceRoute>
          }
        />
        <Route
          path={APP_ROUTES.home}
          element={
            <MaintenanceRoute>
              <ProtectedRoute>
                <LayoutWrapper />
              </ProtectedRoute>
            </MaintenanceRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="gas-account" element={<GasAccount />} />
          <Route path="policies" element={<Policies />} />
          <Route path="policies/create" element={<PolicyCreate />} />
          <Route path="policies/:id/edit" element={<PolicyCreate />} />
          <Route path="policies/:id" element={<PolicyDetails />} />
          <Route path="ads" element={<Ads />} />
          <Route path="ads/create" element={<AdCreate />} />
          <Route path="ads/:id" element={<AdDetails />} />
          <Route path="ads/:id/edit" element={<AdEdit />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="settings" element={<Settings />} />
        </Route>
        <Route path={APP_ROUTES.notFound} element={<NotFound />} />
      </Routes>
    </Suspense>
  )
}

function AppWithRouter() {
  useMaintenanceMode()
  return <AppRoutes />
}

export function App() {
  return (
    <div className="h-full overflow-hidden">
      {createPortal(
        <Toaster
          position="top-center"
          richColors
          style={{ zIndex: 2147483647 }}
          toastOptions={{ style: { zIndex: 2147483647 } }}
        />,
        document.body
      )}
      <BrowserRouter>
        <ScrollToTop />
        <AppWithRouter />
      </BrowserRouter>
    </div>
  )
}
